import { DialogContent, DialogTitle, Stack, Typography, CircularProgress, Button, Box, Avatar, Alert, AlertTitle } from "@mui/material";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { AttachMoney } from "@mui/icons-material";
import { PatchAppointmentStatus } from "../../services/fhir";
import { CommonDialog } from "../common/CommonDialog";
import { FormButtonGroup } from "../form";
import { isMobile } from "react-device-detect";
import { DeleteWhereby } from "../../services/whereby/DeleteWhereby";
import { PutPaymentNotice } from "@/services/fhir/paymentnotice/PutPaymentNotice";
import { useDeleteEvent } from "@/services/google/DeleteEvent";
import { useSessionContext } from "@/contexts/SessionContext";
import { GetPaymentNotice } from "@/services/fhir/paymentnotice/GetPaymentNotice";
import "dayjs/locale/pt-br";

interface IProps {
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  setRefetch?: any;
  appointment: any;
  payment: any;
  isPaid: boolean;
}

export default function ScheduleCancelDialog({
  isOpen,
  setIsOpen,
  appointment,
  setRefetch,
  payment,
  isPaid,
}: IProps) {
  const [loading, setLoading] = useState<boolean>(false);
  const { accessTokenGoogle, user } = useSessionContext();
  const { deleteEvent } = useDeleteEvent();
  const [paymentNotice, setPaymentNotice] = useState<any>([]);
  const [loadingPayment, setLoadingPayment] = useState<boolean>(true);
  const [isPaidNotice, setIsPaidNotice] = useState<boolean>(false);
  
  const fetchPayment = async () => {
    setLoadingPayment(true);
    try {
      if (appointment?.id) {
        // Encontra o participant que é o paciente
        const patientParticipant = appointment.participant?.find((p: any) =>
          p.actor?.reference?.startsWith("Patient/")
        );

        // Encontra o slot no appointment
        const slotReference = appointment.slot?.[0]?.reference;

        if (patientParticipant?.actor?.reference && slotReference) {
          const filter = `status=active&_sort=-_lastUpdated&request=${slotReference}&response=${patientParticipant.actor.reference}&_count=1`;
          const response = await GetPaymentNotice({ filter });
          setPaymentNotice(response?.data?.entry);

          // Verifica se existe paymentStatus no PaymentNotice
          const hasPaymentStatus =
            response?.data?.entry?.[0]?.resource?.paymentStatus?.coding?.[0]?.code === "cleared";
          setIsPaidNotice(!!hasPaymentStatus);
        }
      }
    } catch (error) {
      console.error("Erro ao buscar pagamento:", error);
    } finally {
      setLoadingPayment(false);
    }
  };

  const auxPayment = payment?.length > 0 ? payment : paymentNotice;
  const auxIsPaid = isPaid || isPaidNotice;

  useEffect(() => {
    fetchPayment();
  }, [appointment?.id, isOpen]);

  const performCancellation = async (skipPaymentCancel: boolean = false) => {
      if (!skipPaymentCancel && auxPayment?.length > 0) {
        const paymentCopy = { ...auxPayment?.[0]?.resource };
        paymentCopy.status = "cancelled";
        await PutPaymentNotice({
          paymentId: auxPayment?.[0]?.resource?.id,
          data: paymentCopy,
        });
      }

      const googleCalendarExt = appointment?.extension?.find((ext: any) =>
        ext.url.includes("googleCalendarId")
      );

      if (
        googleCalendarExt?.valueString &&
        accessTokenGoogle &&
        user?.refreshTokenGoogle
      ) {
        try {
          await deleteEvent({
            eventId: googleCalendarExt.valueString,
            accessTokenGoogle,
            refreshToken: user.refreshTokenGoogle,
          });
        } catch (err) {
          console.error("Erro ao excluir evento do Google Calendar:", err);
        }
      }

      if (appointment?.id) {
        await PatchAppointmentStatus({
          appointmentId: appointment.id,
          status: "cancelled",
        });
      }

      if (appointment?.virtualService) {
        DeleteWhereby(appointment?.virtualService?.[0]?.sessionKey as string);
      }
  }

  const onSubmit = async () => {
    setLoading(true);
    try {
      await performCancellation(false);

      toast.success("Consulta cancelada com sucesso!");
      setRefetch?.((prev: boolean) => !prev);
      setIsOpen(false);
    } catch (error) {
      console.error("Erro ao cancelar consulta:", error);
      toast.error("Erro ao cancelar consulta");
    } finally {
      setLoading(false);
    }
  };

  function handleClose() {
    setIsOpen(false);
  }

  return (
    <CommonDialog
      handleClose={handleClose}
      isOpen={isOpen}
      mobileView={isMobile}
    >
      <DialogTitle fontWeight={600} textAlign="center">
        Cancelar consulta
      </DialogTitle>
      <DialogContent>
        {loadingPayment ? (
          <Stack alignItems="center" py={4} spacing={2}>
             <CircularProgress size={32} />
             <Typography variant="body2" color="text.secondary">Verificando status...</Typography>
          </Stack>
        ) : (
          <>
            {auxIsPaid ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle>Pagamento Confirmado</AlertTitle>
                Esta consulta possui pagamento confirmado e não pode ser cancelada diretamente.
              </Alert>
            ) : (
              <Typography textAlign="center" variant="subtitle2" mb={2}>
                Tem certeza que deseja cancelar esta consulta?
              </Typography>
            )}
            
            <Stack spacing={2} mt={2}>
              {auxIsPaid ? (
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => setIsOpen(false)}
                  disabled={loading}
                >
                  Fechar
                </Button>
              ) : (
                <FormButtonGroup
                  onGoBackButtonClick={() => setIsOpen(false)}
                  onNextButtonClick={onSubmit}
                  goBackButtonText="Voltar"
                  nextButtonText="Confirmar Cancelamento"
                  loading={loading}
                />
              )}
            </Stack>
          </>
        )}
      </DialogContent>
    </CommonDialog>
  );
}